import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import React from 'react';
import ReactMarkdown from 'react-markdown';

import { queryCMS } from 'utils/cms/api';
import { articlePageQuery, articleSlugsQuery } from 'utils/cms/gql';
import { ISR_TIMEOUT } from 'utils/config';
import { pageMotionProps } from 'utils/styles/animations';

import * as Styled from './Article.styles';

export interface ArticleProps {
  className?: string;
  article: Article;
  common: CommonType;
}

const defaultProps: Partial<ArticleProps> = {};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const response = await queryCMS({
    query: articlePageQuery(params.slug),
  });

  return {
    props: {
      head: response.articles[0].ogTags,
      navItems: response.menuItems,
      footerData: response.footer,
      socialMedias: response.socialMedias,
      article: response.articles[0],
      common: response.sharedComponent,
    },
    revalidate: ISR_TIMEOUT,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const response = await queryCMS({
    query: articleSlugsQuery(),
  });

  return {
    paths: response.articles.map(article => ({
      params: { slug: article.slug },
    })),
    fallback: 'blocking',
  };
};

const Article: React.FC<ArticleProps> = ({
  className,
  article,
  common,
}: ArticleProps) => {
  const { back } = useRouter();

  const renderParagraph = (section: ArticleParagraphSectionType) => (
    <Styled.ArticleParagraph key={`p-${section.paragraph}`}>
      <ReactMarkdown>{section.paragraph}</ReactMarkdown>
    </Styled.ArticleParagraph>
  );

  const renderSubtitle = (section: ArticleTitleSectionType) => (
    <Styled.ArticleSubtitle key={`subtitle-${section.title}`}>
      {section.title}
    </Styled.ArticleSubtitle>
  );

  const renderImage = (section: ArticleImageSectionType) => (
    <Styled.ArticleImage
      isLanding={section.orientation === 'landscape'}
      key={`subtitle-${section.caption}`}
    >
      <Styled.ImageCarousel images={section.images} />
    </Styled.ArticleImage>
  );

  const renderSection = section => {
    switch (section.__typename) {
      case 'ComponentArticleParagraph':
        return renderParagraph(section);
      case 'ComponentArticleImages':
        return renderImage(section);
      case 'ComponentArticleSectionTitle':
        return renderSubtitle(section);
      // case 'ComponentArticleSideBySideImages':
      //   return renderSideBySideImages(section);
    }
  };

  return (
    <Styled.Wrapper {...pageMotionProps} className={className}>
      <Styled.BackButton reverse onClick={back}>
        <Styled.Arrow /> Wróć
      </Styled.BackButton>
      <Styled.Title>{article.title}</Styled.Title>
      <Styled.ArticleInfo>
        <p>{new Date(article.createdAt).toDateString()}</p>
        <p>{article.author}</p>
      </Styled.ArticleInfo>
      <Styled.ArticleContent>
        {/* @ts-ignore */}
        {article.content.map(section => renderSection(section))}
      </Styled.ArticleContent>
      <Styled.EndSection>
        <Styled.EncouragementText>
          {article.endActionText}
        </Styled.EncouragementText>
        <Styled.SignInButton
          onClick={() => {
            window.open(common.signInUrl, '_blank');
          }}
        >
          {common.signInCopy}
        </Styled.SignInButton>
      </Styled.EndSection>
    </Styled.Wrapper>
  );
};

Article.defaultProps = defaultProps;

export default Article;
